import { collectAnchorText, mentionTextNodes } from './anchor-dom';

export interface ThreadMarkInput {
  threadId: string;
  start: number;
  end: number;
  text: string;
}

export const THREAD_MARK_ATTR = 'data-comment-thread-ids';
export const THREAD_MARK_SELECTOR = `mark[${THREAD_MARK_ATTR}]`;

// Figma uses the blue scale (primary-*); the panel's info-* tokens are grey here. Overlap gets the next tint step.
const BASE_MARK_CLASS =
  'border-primary-400 box-decoration-clone cursor-pointer rounded-xs border-b-2 border-dashed py-[max(0px,calc((1lh-1.1em)/2))]';
const MARK_CLASS = `bg-primary-100 ${BASE_MARK_CLASS}`;
const OVERLAP_MARK_CLASS = `bg-primary-200 ${BASE_MARK_CLASS}`;
// Inside a datapoint only the dashed rule shows, so the datapoint keeps the colour that identifies it; clause lists
// and schedules switch pointer events off, so their marks switch them back on to stay clickable.
const DATAPOINT_MARK_CLASS = `bg-transparent text-inherit pointer-events-auto ${BASE_MARK_CLASS}`;

export const parseThreadMarkIds = (mark: Element): string[] =>
  (mark.getAttribute(THREAD_MARK_ATTR) ?? '').split(' ').filter(Boolean);

const wrap = (node: Node, threadIds: string[], className?: string): void => {
  const mark = document.createElement('mark');
  mark.setAttribute(THREAD_MARK_ATTR, threadIds.join(' '));
  mark.className = className ?? (threadIds.length > 1 ? OVERLAP_MARK_CLASS : MARK_CLASS);
  node.parentNode?.replaceChild(mark, node);
  mark.appendChild(node);
};

// A clause list or schedule renders rows inside the mention span, where an inline mark around the whole span has
// no line box to paint on; its text runs get marked instead. A plain (text-only) datapoint yields nothing.
const compositeTextNodes = (mention: Element): Text[] =>
  mention.children.length === 0 ? [] : mentionTextNodes(mention);

/**
 * Wraps the anchored ranges of a block's open inline threads in <mark> elements. Works on the HTML
 * string the block renders, so re-renders keep the marks. Anchors that no longer match are skipped.
 */
export const injectThreadMarks = (html: string, anchors: ThreadMarkInput[]): string => {
  if (anchors.length === 0) {
    return html;
  }

  const template = document.createElement('template');
  template.innerHTML = html;
  const map = collectAnchorText(template.content);

  const valid = anchors.filter(
    (anchor) =>
      anchor.start < anchor.end &&
      anchor.end <= map.text.length &&
      map.text.slice(anchor.start, anchor.end) === anchor.text,
  );
  if (valid.length === 0) {
    return html;
  }

  const boundaries = [...new Set(valid.flatMap((anchor) => [anchor.start, anchor.end]))].sort((a, b) => a - b);
  const coveringIds = (position: number): string[] =>
    valid.filter((anchor) => anchor.start <= position && position < anchor.end).map((anchor) => anchor.threadId);

  map.segments.forEach((segment) => {
    const pieces: Array<{ node: Text; start: number }> = [];
    let node = segment.node;
    let start = segment.start;
    boundaries
      .filter((boundary) => boundary > segment.start && boundary < segment.end)
      .forEach((boundary) => {
        const rest = node.splitText(boundary - start);
        pieces.push({ node, start });
        node = rest;
        start = boundary;
      });
    pieces.push({ node, start });

    pieces.forEach((piece) => {
      const ids = coveringIds(piece.start);
      if (ids.length > 0 && piece.node.data.length > 0) {
        wrap(piece.node, ids);
      }
    });
  });

  map.mentions.forEach((mention) => {
    const ids = coveringIds(mention.start);
    if (ids.length === 0) {
      return;
    }
    const textNodes = compositeTextNodes(mention.element);
    if (textNodes.length === 0) {
      wrap(mention.element, ids);
      return;
    }
    textNodes.forEach((node) => wrap(node, ids, DATAPOINT_MARK_CLASS));
  });

  return template.innerHTML;
};
